// ** React Imports
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// ** Custom Hooks
import { useSkin } from '@hooks/useSkin';

// ** Third Party Components
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { AlertCircle, X, CheckCircle } from 'react-feather';
import { Formik } from 'formik';

// ** Actions
import { handleLogin } from '@store/authentication';

// ** Context
import { AbilityContext } from '@src/utility/context/Can';

// ** Custom Components
import Avatar from '@components/avatar';
import InputPasswordToggle from '@components/input-password-toggle';
import logo from '@src/assets/images/logo/logo.png';

// ** Utils
import { getHomeRouteForLoggedInUser } from '@utils';

// Graphql
import { loginMutation } from '../../../graphql/Mutation';

// ** Reactstrap Imports
import {
  Row,
  Col,
  Form,
  Input,
  Label,
  Button,
  CardText,
  CardTitle,
  CardFooter,
  Card,
  CardBody
} from 'reactstrap';

// ** Styles
import '@styles/react/pages/page-authentication.scss';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';

const schema = Yup.object().shape({
  email: Yup.string()
    .email('Email invalido')
    .required('Ingresa un email valido'),
  password: Yup.string().required('La clave es requerida')
});

const ToastContent = ({ t, name, role }) => {
  return (
    <div className="d-flex">
      <div className="me-1">
        <Avatar size="sm" color="success" icon={<CheckCircle size={12} />} />
      </div>
      <div className="d-flex flex-column">
        <div className="d-flex justify-content-between">
          <h6>{name}</h6>
          <X
            size={12}
            className="cursor-pointer"
            onClick={() => toast.dismiss(t.id)}
          />
        </div>
        <span>Has entrado con éxito como {role} en Subliful.</span>
      </div>
    </div>
  );
};

const ToastError = ({ message, t }) => {
  return (
    <div className="d-flex">
      <div className="me-1">
        <Avatar
          size="sm"
          color="danger"
          icon={<AlertCircle size={12} />}
          onClick={() => toast.dismiss(t.id)}
        />
      </div>
      <div className="d-flex flex-column">
        <div className="d-flex justify-content-between">
          <h6>Error</h6>
        </div>
        <span>{message}</span>
      </div>
    </div>
  );
};

const Login = () => {
  // ** Hooks
  const { skin } = useSkin();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ability = useContext(AbilityContext);
  const [useLogin] = useMutation(loginMutation);
  const illustration = skin === 'dark' ? 'login-v2-dark.svg' : 'login-v2.png',
    source = require(`@src/assets/images/pages/${illustration}`).default;

  const onSubmit = async (data, { setSubmitting }) => {
    if (Object.values(data).every((field) => field.length > 0)) {
      try {
        const {
          data: { login: res }
        } = await useLogin({
          variables: {
            loginAdminInput: data
          }
        });

        const abilities = [
          {
            action: 'manage',
            subject: 'all'
          }
        ];
        dispatch(
          handleLogin({
            ...res,
            ability: abilities
          })
        );

        ability.update(abilities);

        navigate(getHomeRouteForLoggedInUser(res.profile));
        toast((t) => (
          <ToastContent
            t={t}
            role={res.profile || 'ADMIN'}
            name={res.fullName || res.username || 'John Doe'}
          />
        ));
      } catch (e) {
        const errors = e.graphQLErrors;

        errors.forEach((error) => {
          const message = error.extensions.exception.response.es;
          toast((t) => <ToastError t={t} message={message} />);
        });
        setSubmitting(false);
      }
    } else {
      for (const key in data) {
        if (data[key].length === 0) {
          setError(key, {
            type: 'manual'
          });
        }
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-wrapper auth-v1 px-2 flex-column">
      <Row>
        <Col lg={6} className="p-0">
          <img className="fallback-logo" height={80} src={logo} alt="logo" />
        </Col>
      </Row>
      <div className="auth-inner py-2">
        <Card className="mb-0">
          <CardBody className="p-0">
            <Row>
              <Col lg="6" className="p-0 position-relative">
                <h1
                  className="position-absolute m-2"
                  style={{
                    color: 'rgb(255,255,255)',
                    lineHeight: '42px'
                  }}
                >
                  {'La primera app '}
                  <span style={{ fontWeight: '900' }}>hecha con el alma</span>
                  {' para el alma'}
                </h1>
                <img
                  className="img-fluid"
                  src={source}
                  alt="Login Cover"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    overflow: 'hidden'
                  }}
                />
              </Col>
              <Col lg="6">
                <div className="m-1">
                  <CardTitle tag="h4" className=" mb-1">
                    Login
                  </CardTitle>
                  <CardText tag="p" className="mb-2">
                    Bienvenido, por favor ingrese a su cuenta.
                  </CardText>
                  <Formik
                    validateOnChange
                    validateOnBlur
                    initialValues={{ email: '', password: '' }}
                    validationSchema={schema}
                    onSubmit={onSubmit}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      handleChange,
                      handleBlur,
                      handleSubmit,
                      isSubmitting
                      /* and other goodies */
                    }) => (
                      <Form
                        className="auth-login-form mt-2"
                        onSubmit={handleSubmit}
                      >
                        <div className="mb-1">
                          <Input
                            autoFocus
                            type="email"
                            placeholder="email"
                            name="email"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            invalid={errors.email && true}
                            value={values.email}
                          />
                          <Label
                            className="form-label text-danger"
                            for="login-email"
                          >
                            {errors.email && touched.email && (
                              <>{'Requerido'}</>
                            )}
                          </Label>
                        </div>
                        <div className="mb-1">
                          <InputPasswordToggle
                            className="input-group-merge"
                            invalid={errors.password && true}
                            onChange={handleChange}
                            name="password"
                            onBlur={handleBlur}
                            placeholder="password"
                            value={values.password}
                          />
                          <Label
                            className="form-label text-danger"
                            for="login-email"
                          >
                            {errors.password && touched.password && (
                              <>{'Requerido'}</>
                            )}
                          </Label>
                        </div>
                        <Button
                          type="submit"
                          color="primary"
                          disabled={isSubmitting}
                        >
                          Ingresar
                        </Button>
                      </Form>
                    )}
                  </Formik>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </div>
      <CardFooter className="d-lg-flex align-items-center justify-content-center">
        All righty reserved. Powered by CRIZZ
      </CardFooter>
    </div>
  );
};

export default Login;
